<template>
  <div class="modal fade" id="addBudgetExpense" data-bs-backdrop="static" data-bs-keyboard="false"
    tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content" v-if="show">
        <div class="modal-header shadow-sm">
          <h6 class="modal-title font-weight-bold">
            <p class="mb-1">{{ monthHasEntries ? "Edit" : "Add" }} Monthly Expenses</p>
          </h6>
        </div>
        <div class="modal-body">
          <div class="form-group mb-3">
            <label class="small mb-1">Month<sup class="text-danger">*</sup></label>
            <select class="form-control form-control-sm" v-model.number="month" :disabled="monthLocked">
              <option :value="null" disabled>-- Select month</option>
              <option v-for="m in 12" :key="m" :value="m">{{ monthLabel(m) }}</option>
            </select>
          </div>

          <div v-if="month">
            <label class="small mb-2">Amounts (RM)</label>
            <template v-for="cat in EXPENSE_CATEGORIES">
              <div v-if="cat !== 'kerja_rutin'" class="d-flex align-items-center mb-2" :key="cat">
                <span class="flex-grow-1 pe-2 text-truncate small">{{ CATEGORY_LABELS[cat] }}</span>
                <div class="input-group input-group-sm flex-nowrap" style="width: 180px">
                  <span class="input-group-text">RM</span>
                  <input type="text" inputmode="decimal" class="form-control text-end"
                    placeholder="0.00" :value="displayAmount(cat)"
                    @input="onAmountInput(cat, $event)"
                    @focus="focusedCat = cat" @blur="focusedCat = null" />
                </div>
              </div>
              <div v-else :key="cat" class="mb-2">
                <span class="small">{{ CATEGORY_LABELS.kerja_rutin }}</span>
                <div class="d-flex align-items-center mb-1 ps-3" v-for="d in kerjaRutinDistricts" :key="`kr-${d.id}`">
                  <span class="flex-grow-1 pe-2 text-truncate small text-secondary">{{ d.name }}</span>
                  <div class="input-group input-group-sm flex-nowrap" style="width: 180px">
                    <span class="input-group-text">RM</span>
                    <input type="text" inputmode="decimal" class="form-control text-end"
                      placeholder="0.00" :value="displayDistrictAmount(d.id)"
                      @input="onDistrictAmountInput(d.id, $event)"
                      @focus="focusedCat = `kerja_rutin-${d.id}`" @blur="focusedCat = null" />
                  </div>
                </div>
              </div>
            </template>
            <p class="small text-secondary mb-0">Clear a field to remove that category's entry for this month.</p>
          </div>
        </div>
        <div class="modal-footer shadow">
          <button type="button" class="btn btn-link btn-sm" @click="closeModal()">Cancel</button>
          <button type="button" class="btn btn-sm btn-primary" @click="saveChanges()"
            :disabled="isProcessing || !month">
            <div class="spinner-border spinner-border-sm text-light" v-if="isProcessing"></div>
            <span v-else>{{ monthHasEntries ? "Update" : "Add" }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap/dist/js/bootstrap.esm.min";
import Swal from "sweetalert2";
import {
  EXPENSE_CATEGORIES,
  CATEGORY_LABELS,
  formatAmountInput,
  parseAmountInput,
  formatAmount2,
} from "@/utils/budget";

export default {
  name: "add-budget-expense-modal",
  // Edits one month's manual expenses across all categories at once.
  props: ["show", "hqId", "year", "presetMonth", "expenses", "districts"],
  data() {
    return {
      EXPENSE_CATEGORIES,
      CATEGORY_LABELS,
      modal: null,
      isProcessing: false,
      focusedCat: null,
      month: null,
      amounts: this.blankAmounts(),
    };
  },
  watch: {
    show: {
      handler() {
        this.focusedCat = null;
        if (this.modal && this.show) {
          this.month = this.presetMonth || null;
          this.loadAmounts();
          this.modal.show();
        } else if (this.modal) {
          this.modal.hide();
          this.month = null;
          this.amounts = this.blankAmounts();
        }
      },
      immediate: true,
    },
    month() {
      this.loadAmounts();
    },
  },
  computed: {
    monthLocked() {
      return !!this.presetMonth;
    },
    // Existing expense rows for the selected month (across all categories).
    monthEntries() {
      if (!this.month) return [];
      return (this.expenses || []).filter((e) => e.month === this.month);
    },
    monthHasEntries() {
      return this.monthEntries.length > 0;
    },
    // Real districts + "Unassigned" only if the month being edited already
    // has a legacy no-district Kerja Rutin entry — new entries always require
    // a real district, "Unassigned" is edit/clear-only.
    kerjaRutinDistricts() {
      const hasUnassigned = this.monthEntries.some(
        (e) => e.category === "kerja_rutin" && (!e.district || e.district.id == null)
      );
      const list = (this.districts || []).map((d) => ({ id: d.id, name: d.name }));
      if (hasUnassigned) list.push({ id: "unassigned", name: "Unassigned" });
      return list;
    },
  },
  methods: {
    blankAmounts() {
      const a = {};
      EXPENSE_CATEGORIES.forEach((c) => {
        a[c] = c === "kerja_rutin" ? {} : null;
      });
      return a;
    },
    // Load the selected month's existing amounts into the form (empty = no entry).
    loadAmounts() {
      const a = this.blankAmounts();
      this.monthEntries.forEach((e) => {
        if (e.category === "kerja_rutin") {
          const id = e.district && e.district.id != null ? e.district.id : "unassigned";
          a.kerja_rutin[id] = Number(e.amount) || null;
        } else if (a[e.category] !== undefined) {
          a[e.category] = Number(e.amount) || null;
        }
      });
      this.amounts = a;
    },
    // At rest show 2 decimals; while focused show the free-typing format. Empty stays empty.
    displayAmount(cat) {
      const v = this.amounts[cat];
      if (v == null || v === "") return "";
      return this.focusedCat === cat ? formatAmountInput(String(v)) : formatAmount2(v);
    },
    onAmountInput(cat, event) {
      const formatted = formatAmountInput(event.target.value);
      this.amounts[cat] = parseAmountInput(formatted);
      event.target.value = formatted;
    },
    displayDistrictAmount(districtId) {
      const v = this.amounts.kerja_rutin[districtId];
      if (v == null || v === "") return "";
      return this.focusedCat === `kerja_rutin-${districtId}` ? formatAmountInput(String(v)) : formatAmount2(v);
    },
    onDistrictAmountInput(districtId, event) {
      const formatted = formatAmountInput(event.target.value);
      this.$set(this.amounts.kerja_rutin, districtId, parseAmountInput(formatted));
      event.target.value = formatted;
    },
    monthLabel(m) {
      return this.Helper.moment(
        `${this.year || this.Helper.currentYear}-${String(m).padStart(2, "0")}-01`
      ).format("MMMM");
    },
    closeModal(response) {
      if (!this.isProcessing) this.$emit("close", response);
    },
    // Upsert each category for the month: POST new, PUT changed, DELETE cleared.
    async saveChanges() {
      if (!this.month) return;
      this.isProcessing = true;
      try {
        const existingByCat = {};
        const existingKerjaRutinByDistrict = {};
        this.monthEntries.forEach((e) => {
          if (e.category === "kerja_rutin") {
            const id = e.district && e.district.id != null ? e.district.id : "unassigned";
            existingKerjaRutinByDistrict[id] = e;
          } else {
            existingByCat[e.category] = e;
          }
        });

        const ops = [];
        EXPENSE_CATEGORIES.filter((cat) => cat !== "kerja_rutin").forEach((cat) => {
          const amount = this.amounts[cat];
          const row = existingByCat[cat];
          if (amount == null) {
            if (row) ops.push(this.API.del(`nod-budget-expenses/${row.id}`));
          } else if (row) {
            ops.push(this.API.put(`nod-budget-expenses/${row.id}`, { amount }));
          } else {
            ops.push(
              this.API.post("nod-budget-expenses", {
                hq: this.hqId,
                year: this.year,
                month: this.month,
                category: cat,
                district: null,
                amount,
              })
            );
          }
        });

        this.kerjaRutinDistricts.forEach((d) => {
          const amount = this.amounts.kerja_rutin[d.id];
          const row = existingKerjaRutinByDistrict[d.id];
          if (amount == null) {
            if (row) ops.push(this.API.del(`nod-budget-expenses/${row.id}`));
          } else if (row) {
            ops.push(this.API.put(`nod-budget-expenses/${row.id}`, { amount }));
          } else if (d.id !== "unassigned") {
            ops.push(
              this.API.post("nod-budget-expenses", {
                hq: this.hqId,
                year: this.year,
                month: this.month,
                category: "kerja_rutin",
                district: d.id,
                amount,
              })
            );
          }
        });

        await Promise.all(ops);
        this.isProcessing = false;
        Swal.fire(this.monthHasEntries ? "Expenses updated" : "Expenses added", "", "success");
        this.closeModal(true);
      } catch (e) {
        console.error(e);
        Swal.fire("Error saving expenses", "", "error");
      }
      this.isProcessing = false;
    },
  },
  mounted() {
    this.modal = new Modal(document.getElementById("addBudgetExpense"));
  },
};
</script>
